import { all, call, put, takeLatest } from 'redux-saga/effects'

import { postBusinessRegistration, postBusinessRegistrationForm } from 'api/apiDataHelpers'
import {
    getJson,
    getRequest,
    getRequestV2,
    getRequestV3,
    getRequestWithBlob,
    postRequest,
    postRequestV2,
    postRequestV3,
    putRequest,
    putRequestV3,
} from 'api/apiUtils'
import { isDevelopment, isTranslationModeEnabled } from 'utils/common'
import { makeQueryParamsString } from 'utils/stringHelpers'

import { startFetchUserAccounts } from '../user/user-actions'
import UserActionTypes from '../user/user-types'

import {
    errorServicePlan,
    failureArchiveSupportConversations,
    failureBusinessRegistration,
    failureChangeMainCurrency,
    failureChangePassword,
    failureCompanyMember,
    failureCompanyRegisterForm,
    failureDocumentRequests,
    failureDocumentRequestsForm,
    failureDynamicFormSubmit,
    failureEnvJson,
    failureGetDynamicForm,
    failureGetFile,
    failureNewSupportConversations,
    failurePutCompanyMemberGroup,
    failureRemoveCompanyMember,
    failureReplySupportConversations,
    failureSubmintCompanyFormDocument,
    failureSupportConversations,
    failureSupportConversationsDetails,
    failureSupportConversationsFile,
    failureSupportUnreadConversationsCount,
    failureUploadDocuments,
    failureUploadSourceOfFunds,
    failureUserSettings,
    fetchDocumentRequests,
    fetchSupportedCurrencies as fetchSupportedCurrenciesAction,
    fetchSupportedLanguages as fetchSupportedLanguagesActon,
    finishArchiveSupportConversations,
    successArchiveSupportConversations,
    successBusinessRegistration,
    successBusinessRegistrationDeposits,
    successChangeMainCurrency,
    successChangePassword,
    successCompanyMember,
    successCompanyRegisterForm,
    successDocumentRequests,
    successDocumentRequestsForm,
    successDynamicFormSubmit,
    successEnvJson,
    successFetchServiceFeesSchedule,
    successFetchServiceFeesScheduleNo,
    successFetchServicePlans,
    successGetCompanyUsers,
    successGetCompanyUsersGroups,
    successGetDocumentFormData,
    successGetDynamicForm,
    successGetFile,
    successGetNotifications,
    successGetUserSettings,
    successNewSupportConversations,
    successPostNotifications,
    successPutCompanyMemberGroup,
    successRemoveCompanyMember,
    successReplySupportConversations,
    successServicePlan,
    successSubmintCompanyFormDocument,
    successSupportConversations,
    successSupportConversationsDetails,
    successSupportConversationsFile,
    successSupportDepartments,
    successSupportUnreadConversationsCount,
    successSupportedCurrencies,
    successSupportedLanguages,
    successUploadDocuments,
    successUploadSourceOfFunds,
    successUserSettings,
    successfetchSystemSettings,
} from './settings-actions'
import SettingsActionTypes from './settings-types'

function* fetchDocumentRequestsAction({ payload: query }) {
    try {
        const { search, sortDirection, sortProperty, skip, limit, documentRequestStatus = 'Active' } = { ...query }
        const queryParams = makeQueryParamsString({ search, sortDirection, sortProperty, skip, limit, documentRequestStatus })
        const { data } = yield getRequestV2(`/documentRequests${queryParams}`)

        if (documentRequestStatus === 'Active') {
            yield put(successDocumentRequests({ active: data }))
        }
        if (documentRequestStatus === 'Completed') {
            yield put(successDocumentRequests({ completed: data }))
        }
    } catch (err) {
        yield put(failureDocumentRequests({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error fetching Document Requests', err?.response?.data)
        }
    }
}

export function* onFetchDocumentRequests() {
    yield takeLatest([SettingsActionTypes.FETCH_DOCUMENT_REQUEST], fetchDocumentRequestsAction)
}

function* startUploadDocument({ payload: { formData, documentNo } }) {
    try {
        yield postRequest(`/documentRequests/${documentNo}/documents`, formData)
        yield put(successUploadDocuments())
    } catch (err) {
        yield put(failureUploadDocuments({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error on document submit', err?.response?.data)
        }
    }
}

export function* onStartUploadDocument() {
    yield takeLatest(SettingsActionTypes.START_UPLOAD_DOCUMENTS, startUploadDocument)
}

function* startSubmintCompanyFormDocument({ payload: { formData } }) {
    try {
        yield postRequest('/user/companies', formData)
        yield put(successSubmintCompanyFormDocument())
        yield put(startFetchUserAccounts())
    } catch (err) {
        yield put(failureSubmintCompanyFormDocument({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error on company form submit', err?.response?.data)
        }
    }
}

export function* onStartSubmintCompanyFormDocument() {
    yield takeLatest(SettingsActionTypes.START_SUBMIT_COMPANY_FORM_DOCUMENT, startSubmintCompanyFormDocument)
}

function* startChangePassword({ payload: data }) {
    try {
        yield putRequest('/user/password', data)
        yield put(successChangePassword())
    } catch (err) {
        yield put(failureChangePassword({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error on password change', err?.response?.data || 'Failed change password')
        }
    }
}

export function* onStartChangePassword() {
    yield takeLatest(SettingsActionTypes.START_CHANGE_PASSWORD, startChangePassword)
}

function* fetchEnvJson() {
    try {
        const { data } = yield getJson('/env.json')

        localStorage.setItem('envJSON', JSON.stringify(data) || 'null')
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('fetchEnvJson')
        }
    }
}

function* getEnvJson() {
    try {
        const { data } = yield getJson('/env.json')

        yield put(successEnvJson(data))
    } catch (err) {
        yield put(failureEnvJson())
        if (process.env.NODE_ENV !== 'production') {
            console.log('failureEnvJson')
        }
    }
}

export function* onGetEnvJson() {
    yield takeLatest(SettingsActionTypes.GET_ENV_JSON, getEnvJson)
}

function* fetchSupportedCurrencies() {
    try {
        const { data } = yield getRequest('/currencies')

        yield put(successSupportedCurrencies(data))
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            console.log(err.response?.data)
        }
    }
}

export function* onFetchSupportedCurrencies() {
    yield takeLatest(SettingsActionTypes.FETCH_SUPPORTED_CURRENCIES, fetchSupportedCurrencies)
}

function* fetchSupportedLanguages() {
    try {
        // const enabledLang = process.env.REACT_APP_LANG.split(',')
        const enabledLang = ['en', 'de', 'es', 'it', 'ru', 'ar', 'zh', 'fr', 'ur', 'tr']

        const supportedLang = [
            {
                value: 'English',
                code: 'en',
            },
            {
                value: 'Lietuvių',
                code: 'lt',
            },
            {
                value: 'Español',
                code: 'es',
            },
            {
                value: 'Italiano',
                code: 'it',
            },
            {
                value: 'Pусский',
                code: 'ru',
            },
            {
                value: 'German',
                code: 'de',
            },
            {
                value: 'العربيه',
                code: 'ar',
            },
            {
                value: '中国人',
                code: 'zh',
            },
            {
                value: 'Français',
                code: 'fr',
            },

            {
                value: 'اردو',
                code: 'ur',
            },

            {
                value: 'Türkçe',
                code: 'tr',
            },
            ...(isTranslationModeEnabled ? [{ value: 'DEV', code: 'dev' }] : []),
        ]

        const data = supportedLang?.filter(el => enabledLang?.includes(el.code))

        yield put(successSupportedLanguages(data))
    } catch (err) {
        if (isDevelopment) {
            console.log(err?.response?.data)
        }
    }
}

export function* onFetchSupportedLanguages() {
    yield takeLatest(SettingsActionTypes.FETCH_SUPPORTED_LANGUAGES, fetchSupportedLanguages)
}

function* fetchSupportConversations({ payload: { skip = 0, limit = 10, status = 'Active' } }) {
    try {
        const {
            data,
            data: { unreadCount },
        } = yield getRequest(`/user/conversations?skip=${skip}&limit=${limit}&status=${status}`)

        yield put(successSupportConversations(data))
        if (status === 'Active') {
            yield put(successSupportUnreadConversationsCount(unreadCount))
        }
    } catch (err) {
        yield put(failureSupportConversations({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error fetchSupportConversations', err?.response?.data)
        }
    }
}

export function* onFetchSupportConversations() {
    yield takeLatest(SettingsActionTypes.FETCH_SUPPORT_CONVERSATIONS, fetchSupportConversations)
}

function* fetchUnreadConversationsCount() {
    try {
        const {
            data: { unreadCount },
        } = yield getRequest('/user/conversations?skip=0&limit=0&status=Active')

        yield put(successSupportUnreadConversationsCount(unreadCount))
    } catch (err) {
        yield put(failureSupportUnreadConversationsCount({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error fetchSupportConversations', err?.response?.data)
        }
    }
}

export function* onFetchUnreadConversationsCount() {
    yield takeLatest(SettingsActionTypes.FETCH_SUPPORT_UNREAD_CONVERSATIONS_COUNT, fetchUnreadConversationsCount)
}

function* getSupportConversationDetails({ payload: { id } }) {
    try {
        const { data } = yield getRequest(`/user/conversations/${id}`)

        yield put(successSupportConversationsDetails(data))
    } catch (err) {
        yield put(failureSupportConversationsDetails({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error getSupportConversation', err?.response?.data)
        }
    }
}

export function* onGetSupportConversationDetails() {
    yield takeLatest(SettingsActionTypes.GET_SUPPORT_CONVERSATION_DETAILS, getSupportConversationDetails)
}

function* startNewSupportConversations({ payload: { formData } }) {
    try {
        yield postRequest('/user/conversations', formData)
        yield put(successNewSupportConversations())
    } catch (err) {
        yield put(failureNewSupportConversations({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error send message', err?.response?.data)
        }
    }
}

export function* onStartNewSupportConversations() {
    yield takeLatest(SettingsActionTypes.START_NEW_SUPPORT_CONVERSATIONS, startNewSupportConversations)
}

function* startReplySupportConversation({ payload: { conversationID, formData } }) {
    try {
        yield postRequest(`/user/conversations/${conversationID}/messages`, formData)
        yield put(successReplySupportConversations())
        yield getSupportConversationDetails({ payload: { id: conversationID } })
    } catch (err) {
        yield put(failureReplySupportConversations({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error send message', err?.response?.data)
        }
    }
}

export function* onStartReplySupportConversation() {
    yield takeLatest(SettingsActionTypes.START_REPLY_SUPPORT_CONVERSATIONS, startReplySupportConversation)
}

function* startSupportConversationFile({ payload: { conversationID, messageId, fileId } }) {
    try {
        const { data } = yield getRequestWithBlob(`/user/conversations/${conversationID}/messages/${messageId}/files/${fileId}`)

        yield put(successSupportConversationsFile(data))
    } catch (err) {
        yield put(failureSupportConversationsFile({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error getSupportConversationFile')
        }
    }
}

export function* onStartSupportConversationFile() {
    yield takeLatest(SettingsActionTypes.START_SUPPORT_CONVERSATION_FILE, startSupportConversationFile)
}

function* startArchiveSupportConversation({ payload: { ids } }) {
    try {
        yield putRequest('/user/conversations/archive', { ids })
        yield put(successArchiveSupportConversations())
        yield put(finishArchiveSupportConversations())
    } catch (err) {
        yield put(failureArchiveSupportConversations({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error send message', err?.response?.data)
        }
    }
}

export function* onStartArchiveSupportConversation() {
    yield takeLatest(SettingsActionTypes.START_ARCHIVE_SUPPORT_CONVERSATIONS, startArchiveSupportConversation)
}

function* fetchSupportDepartments() {
    try {
        const { data } = yield getRequest('/user/conversations/departments')

        yield put(successSupportDepartments(data))
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error fetching Support departments', err?.response?.data)
        }
    }
}

export function* onFetchSupportDepartments() {
    yield takeLatest(SettingsActionTypes.FETCH_SUPPORT_DEPARTMENTS, fetchSupportDepartments)
}

function* fetchServiceFeeBaskets() {
    try {
        const { data } = yield getRequestV2('/user/feeBaskets')

        yield put(successFetchServicePlans(data))
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error feeBaskets', err)
        }
    }
}

export function* onFetchServicePlans() {
    yield takeLatest(SettingsActionTypes.FETCH_SERVICE_PLANS, fetchServiceFeeBaskets)
}

function* fetchServiceFeeSchedule() {
    try {
        const { data } = yield getRequest('/user/fees/schedule')

        yield put(successFetchServiceFeesSchedule(data))
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error fetchServiceFeeSchedule', err)
        }
    }
}

export function* onFetchServiceFeeSchedule() {
    yield takeLatest(SettingsActionTypes.FETCH_SERVICE_FEES_SCHEDULE, fetchServiceFeeSchedule)
}

function* fetchServiceFeeScheduleNo({ payload: { no } }) {
    try {
        const { data } = yield getRequestV2(`/user/feeBaskets/${no}/fees/schedule`)

        yield put(successFetchServiceFeesScheduleNo(data))
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error fetchServiceFeeScheduleNo', err)
        }
    }
}

export function* onFetchServiceFeeScheduleNo() {
    yield takeLatest(SettingsActionTypes.FETCH_SERVICE_FEES_SCHEDULE_NO, fetchServiceFeeScheduleNo)
}

function* fetchSystemSettings() {
    try {
        const { data } = yield getRequest('/settings')

        yield put(successfetchSystemSettings(data))
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error fetchSystemSettings', err)
        }
    }
}

export function* onfetchSystemSettings() {
    yield takeLatest(SettingsActionTypes.FETCH_SYSTEM_SETTINGS, fetchSystemSettings)
}

function* startServicePlan({ payload: no }) {
    try {
        const { data } = yield putRequest('/identities/feesBaskets', { basketNo: no })

        yield put(successServicePlan(data))
    } catch (err) {
        yield put(errorServicePlan({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error feeBasket details', err?.response?.data)
        }
    }
}
export function* onStartServicePlan() {
    yield takeLatest(SettingsActionTypes.START_SERVICE_PLAN, startServicePlan)
}

function* startGetDynamicForm({ payload: { no } }) {
    try {
        const { data } = yield getRequestV3(`/documentRequests/${no}`)

        yield put(successGetDynamicForm(data))
    } catch (err) {
        // TODO: create error toast
        yield put(failureGetDynamicForm({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error startGetDynamicForm', err?.response?.data)
        }
    }
}
export function* onStartGetDynamicForm() {
    yield takeLatest(SettingsActionTypes.START_GET_DYNAMIC_FORM, startGetDynamicForm)
}

function* startUpdateDynamicForm({ payload: { no, data } }) {
    try {
        yield postRequest(`/documentRequests/${no}/partial-form`, {
            formData: data,
        })
    } catch (err) {
        // TODO: create error toast
        yield put(failureGetDynamicForm({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error startUpdateDynamicForm', err?.response?.data)
        }
    }
}
export function* onStartUpdateDynamicForm() {
    yield takeLatest(SettingsActionTypes.START_UPDATE_DYNAMIC_FORM, startUpdateDynamicForm)
}

function* startDocumentFormSubmit({ payload: { no, verificationCode, value, formVersion, taxPayerNumber, identificationNumber } }) {
    try {
        yield postRequest(`/documentRequests/${no}/forms`, {
            verificationCode,
            data: value,
            formVersion,
            taxPayerNumber,
            identificationNumber,
        })
        yield put(successDocumentRequestsForm())
    } catch (err) {
        yield put(failureDocumentRequestsForm({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error startDocumentFormSubmit', err?.response?.data)
        }
    }
}

export function* onStartDocumentFormSubmit() {
    yield takeLatest(SettingsActionTypes.START_DOCUMENT_REQUEST_FORM, startDocumentFormSubmit)
}

function* startDynamicFormSubmit({ payload: { no, taxPayerNumber, identificationNumber, data, verificationCode, templateId } }) {
    try {
        yield postRequestV2(`/documentRequests/${no}/forms`, { taxPayerNumber, identificationNumber, data, verificationCode, templateId })
        yield put(successDynamicFormSubmit())
    } catch (err) {
        yield put(failureDynamicFormSubmit({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error startDynamicFormSubmit', err?.response?.data)
        }
    }
}

export function* onStartDynamicFormSubmit() {
    yield takeLatest(SettingsActionTypes.START_DYNAMIC_FORM_SUBMIT, startDynamicFormSubmit)
}

function* startCompanyFormSubmit({
    payload: { companyName, city, country, address, postCode, payerNumber, phone, email, verificationCode, website, data, no, formVersion },
}) {
    try {
        const responseData = yield postBusinessRegistrationForm({
            companyName,
            city,
            address,
            country,
            postCode,
            payerNumber,
            phone,
            email,
            verificationCode,
            website,
            data,
            formVersion,
        })

        yield postRequestV2(`/documentRequests/${no}/forms/company`, responseData)
        yield put(successCompanyRegisterForm())
    } catch (err) {
        yield put(failureCompanyRegisterForm({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error startCompanyFormSubmit', err?.response?.data)
        }
    }
}

export function* onStartCompanyFormSubmit() {
    yield takeLatest(SettingsActionTypes.START_COMPANY_REGISTER_FORM, startCompanyFormSubmit)
}

function* startBusinessRegistration({
    payload: { companyName, payerNumber, city, countryCode, postCode, street, isRepresentative, currencyCode },
}) {
    try {
        const responseData = yield postBusinessRegistration({
            companyName,
            payerNumber,
            city,
            countryCode,
            postCode,
            street,
            isRepresentative,
            currencyCode,
        })
        const { data } = yield postRequestV3('/user/companies', responseData)

        yield put(successBusinessRegistration(data))
    } catch (err) {
        yield put(failureBusinessRegistration({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error startBusinessRegistration', err?.response?.data)
        }
    }
}

export function* onStartBusinessRegistration() {
    yield takeLatest(SettingsActionTypes.START_BUSINESS_REGISTRATION, startBusinessRegistration)
}

function* startGetDocumentFormdata({ payload: { no } }) {
    try {
        const {
            data: { formData },
        } = yield getRequestV2(`/documentRequests/${no}`)

        yield put(successGetDocumentFormData(formData))
    } catch {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error startGetCompanyFormdata')
        }
    }
}

export function* onStartGetDocumentFormdata() {
    yield takeLatest(SettingsActionTypes.START_GET_DOCUMENT_FORMDATA, startGetDocumentFormdata)
}

function* fetchDataOnPageLoad() {
    yield fetchEnvJson()
    yield put(fetchSupportedLanguagesActon())
}

export function* onFetchDataOnPageLoad() {
    yield takeLatest(SettingsActionTypes.FETCH_ON_PAGE_LOAD, fetchDataOnPageLoad)
}

function* startGetNotifications() {
    try {
        const { data } = yield getRequest('/user/settings/notifications')

        yield put(successGetNotifications(data))
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error get notifications', err?.response?.data)
        }
    }
}

export function* onStartGetNotifications() {
    yield takeLatest(SettingsActionTypes.START_GET_NOTIFICATION, startGetNotifications)
}

function* startPostNotifications({ payload: item }) {
    try {
        yield putRequest('/user/settings/notifications', item)
        const { data } = yield getRequest('/user/settings/notifications')

        yield put(successPostNotifications(data))
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error post notifications', err?.response?.data)
        }
    }
}

export function* onStartPostNotifications() {
    yield takeLatest(SettingsActionTypes.START_POST_NOTIFICATION, startPostNotifications)
}

function* getUserSettings() {
    try {
        const { data } = yield getRequest('/user/settings')

        yield put(successGetUserSettings(data))
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error getUserSettings', err?.response?.data)
        }
    }
}

export function* onGetUserSettings() {
    yield takeLatest(SettingsActionTypes.GET_USER_SETTINGS, getUserSettings)
}

function* startUserSettings({ payload: { showZeroBalances, languageCode } }) {
    try {
        yield putRequest('/user/settings', { showZeroBalances, languageCode })
        yield put(successUserSettings())
        yield getUserSettings()
    } catch (err) {
        yield put(failureUserSettings())
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error startUserSettings', err?.response?.data)
        }
    }
}

export function* onStartUserSettings() {
    yield takeLatest(SettingsActionTypes.START_USER_SETTINGS, startUserSettings)
}

function* startChangeMainCurrency({ payload: data }) {
    try {
        yield putRequest('/identity/currency', { currencyCode: `${data}` })
        yield put(successChangeMainCurrency(data))
    } catch (err) {
        yield put(failureChangeMainCurrency())
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error post notifications', err?.response?.data)
        }
    }
}

export function* onStartChangeMainCurrency() {
    yield takeLatest(SettingsActionTypes.START_CHANGE_MAIN_CURRENCY, startChangeMainCurrency)
}

function* getCompanyUsers() {
    try {
        const {
            data: { members },
        } = yield getRequestV3('/user/companies/members')

        yield put(successGetCompanyUsers(members))
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error getCompanyUsers', err?.response?.data)
        }
    }
}

export function* onGetCompanyUsers() {
    yield takeLatest(SettingsActionTypes.GET_COMPANIES_MEMBERS, getCompanyUsers)
}

function* getCompanyUsersGroups() {
    try {
        const { data } = yield getRequestV3('/user/companies/members/permissions/groups')

        yield put(successGetCompanyUsersGroups(data))
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error getCompanyUsersGroups', err?.response?.data)
        }
    }
}

export function* onGetCompanyUsersGroups() {
    yield takeLatest(SettingsActionTypes.GET_COMPANIES_MEMBERS_GROUPS, getCompanyUsersGroups)
}

function* startCompanyMember({ payload: { email, roleNo, verificationCode, transferOwnership } }) {
    try {
        yield postRequestV3('/user/companies/members', {
            members: [{ email, permissionGroupNo: roleNo, transferOwnership }],
            verificationCode,
        })
        yield put(successCompanyMember())
    } catch (err) {
        yield put(failureCompanyMember({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error startCompanyMember', err?.response?.data)
        }
    }
}

export function* onStartCompanyMember() {
    yield takeLatest(SettingsActionTypes.START_COMPANIES_MEMBER, startCompanyMember)
}

function* startPutCompanyMemberGroup({ payload: { userId, permissionGroupNo, verificationCode } }) {
    try {
        yield putRequestV3(`/user/companies/members/${userId}/permissions/groups`, {
            permissionGroupNo,
            verificationCode,
        })
        yield put(successPutCompanyMemberGroup())
    } catch (err) {
        yield put(failurePutCompanyMemberGroup({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error startPutCompanyMemberGroup', err?.response?.data)
        }
    }
}

export function* onStartPutCompanyMemberGroup() {
    yield takeLatest(SettingsActionTypes.START_PUT_COMPANIES_MEMBER_GROUP, startPutCompanyMemberGroup)
}

function* startRemoveCompanyMember({ payload: { userId, verificationCode } }) {
    try {
        yield postRequestV3(`/user/companies/members/${userId}/remove`, { verificationCode })
        yield put(successRemoveCompanyMember())
    } catch (err) {
        yield put(failureRemoveCompanyMember({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('Error startRemoveCompanyMember', err?.response?.data)
        }
    }
}

export function* onStartRemoveCompanyMember() {
    yield takeLatest(SettingsActionTypes.START_REMOVE_COMPANIES_MEMBER_GROUP, startRemoveCompanyMember)
}

function* signInSuccess() {
    yield put(fetchSupportedCurrenciesAction())
    yield put(fetchDocumentRequests())
    yield fetchUnreadConversationsCount()
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    yield getUserSettings()
}

export function* onSignSuccess() {
    yield takeLatest(UserActionTypes.SIGN_IN_SUCCESS, signInSuccess)
}

function* startGetFiles({ payload: { no } }) {
    try {
        const { data } = yield getRequestWithBlob(`/files/${no}`)

        yield put(successGetFile(data))
    } catch (err) {
        yield put(failureGetFile({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('error Getting file', err?.response?.data)
        }
    }
}

export function* onStartGetFiles() {
    yield takeLatest(SettingsActionTypes.START_GET_FILE, startGetFiles)
}

function* startUploadDocumentSourceofFunds({ payload: { formData } }) {
    try {
        yield postRequest('/documentRequests', formData)
        yield put(successUploadSourceOfFunds())
    } catch (err) {
        yield put(failureUploadSourceOfFunds({ errorMessage: err?.response?.data?.errors }))
        if (process.env.NODE_ENV !== 'production') {
            console.log('failureUploadSourceOfFunds')
        }
    }
}

export function* onStartUploadDocumentSourceofFunds() {
    yield takeLatest(SettingsActionTypes.START_UPLOAD_DOCUMENT_SOURCE_OF_FUNDS, startUploadDocumentSourceofFunds)
}

export function* settingsSagas() {
    yield all([
        call(onSignSuccess),
        call(onStartUploadDocument),
        call(onStartChangePassword),
        call(onFetchDataOnPageLoad),
        call(onFetchSupportedCurrencies),
        call(onFetchSupportedLanguages),
        call(onFetchSupportConversations),
        call(onStartNewSupportConversations),
        call(onFetchServicePlans),
        call(onFetchServiceFeeSchedule),
        call(onFetchServiceFeeScheduleNo),
        call(onStartServicePlan),
        call(onGetSupportConversationDetails),
        call(onFetchDocumentRequests),
        call(onStartSupportConversationFile),
        call(onFetchSupportDepartments),
        call(onStartReplySupportConversation),
        call(onStartArchiveSupportConversation),
        call(onStartGetDynamicForm),
        call(onStartUpdateDynamicForm),
        call(onStartDocumentFormSubmit),
        call(onStartDynamicFormSubmit),
        call(onStartCompanyFormSubmit),
        call(onStartGetNotifications),
        call(onStartPostNotifications),
        call(onStartSubmintCompanyFormDocument),
        call(onStartChangeMainCurrency),
        call(onfetchSystemSettings),
        call(onStartBusinessRegistration),
        call(onGetUserSettings),
        call(onStartUserSettings),
        call(onGetCompanyUsers),
        call(onGetCompanyUsersGroups),
        call(onStartCompanyMember),
        call(onStartPutCompanyMemberGroup),
        call(onStartRemoveCompanyMember),
        call(onGetEnvJson),
        call(onStartGetDocumentFormdata),
        call(onStartGetFiles),
        call(onFetchUnreadConversationsCount),
        call(onStartUploadDocumentSourceofFunds),
    ])
}
